import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MessageCircle } from "lucide-react";
import MessageThread from "@/components/shared/MessageThread";

const statusColor = {
    pending:   "bg-yellow-100 text-yellow-900 border-yellow-500",
    accepted:  "bg-blue-50 text-blue-900 border-blue-500",
    rejected:  "bg-red-50 text-red-900 border-red-500",
    converted: "bg-green-50 text-green-900 border-green-600",
};

export default function AdminDesignRequests() {
    const [rows, setRows] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [noteById, setNoteById] = useState({});
    const [busy, setBusy] = useState("");

    const load = async () => {
        try { const r = await api.get("/design-requests"); setRows(r.data || []); }
        catch { /* silent */ }
    };
    useEffect(() => { load(); }, []);

    const decide = async (id, action) => {
        const note = noteById[id] || "";
        setBusy(`${action}-${id}`);
        try {
            await api.post(`/design-requests/${id}/${action}`, { note });
            toast.success(action === "accept" ? "Design accepted — negotiation started" : "Design rejected");
            setNoteById({ ...noteById, [id]: "" });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || `${action} failed`);
        } finally { setBusy(""); }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <div className="overline text-neutral-500">Bespoke</div>
                    <h1 className="font-display text-5xl tracking-tight leading-none mt-2">Design Requests</h1>
                </div>
                <div className="font-body text-sm text-neutral-500">{rows.length} submissions</div>
            </div>

            {rows.length === 0 && (
                <div className="border border-dashed border-border p-12 text-center">
                    <div className="overline text-neutral-500">Empty</div>
                    <div className="font-display text-2xl mt-2">No design requests yet</div>
                    <div className="text-sm text-neutral-500 mt-2 font-body">Clients can upload bespoke designs from their &quot;Designs&quot; tab.</div>
                </div>
            )}

            <div className="space-y-3">
                {rows.map((d) => (
                    <div key={d.id} className="border border-border" data-testid={`admin-design-row-${d.id}`}>
                        <div className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
                            <div className="md:col-span-1 aspect-square bg-neutral-100 overflow-hidden">
                                {d.image_url && <img src={d.image_url} alt={d.title} className="w-full h-full object-cover"/>}
                            </div>
                            <div className="md:col-span-2">
                                <div className="font-mono text-[10px] text-neutral-500">{d.id.slice(0, 8)}</div>
                                <div className="font-body text-sm mt-1"><b>{d.title}</b></div>
                                <div className="text-[11px] text-neutral-500 mt-1">{d.client_company || d.client_name} · ID {d.client_login_id}</div>
                                <div className="text-[11px] text-neutral-500">{new Date(d.created_at).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="overline text-neutral-500">Qty</div>
                                <div className="tabular-nums font-body text-sm">{d.quantity} pcs</div>
                            </div>
                            <div>
                                <div className="overline text-neutral-500">Target</div>
                                <div className="tabular-nums font-body text-sm">{d.currency === "GBP" ? "£" : d.currency === "USD" ? "$" : "€"}{d.target_price?.toFixed(2)}/pc</div>
                                <div className="text-[10px] text-neutral-500 tabular-nums">total {d.currency === "GBP" ? "£" : d.currency === "USD" ? "$" : "€"}{(d.target_price * d.quantity).toFixed(2)}</div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className={`inline-block px-2 py-1 border uppercase tracking-widest text-[10px] font-semibold text-center ${statusColor[d.status]}`} data-testid={`admin-design-status-${d.id}`}>{d.status}</span>
                                <button onClick={() => setOpenId(openId === d.id ? null : d.id)} className="text-xs uppercase tracking-widest text-neutral-600 hover:text-black inline-flex items-center gap-1 justify-center" data-testid={`admin-design-toggle-${d.id}`}>
                                    <MessageCircle className="h-3 w-3" strokeWidth={1.5}/> {openId === d.id ? "Close" : "Open"}
                                </button>
                            </div>
                        </div>

                        {openId === d.id && (
                            <div className="border-t border-border bg-neutral-50 p-4 space-y-4">
                                {d.notes && (
                                    <div className="p-3 border border-black/30 bg-white text-xs font-body">
                                        <div className="overline text-neutral-500 mb-1">Client notes</div>
                                        {d.notes}
                                    </div>
                                )}
                                {d.status === "pending" && (
                                    <div className="border border-black p-4 bg-white space-y-3">
                                        <textarea
                                            value={noteById[d.id] || ""}
                                            onChange={(e) => setNoteById({ ...noteById, [d.id]: e.target.value })}
                                            placeholder="Note to client (visible on decision)"
                                            rows={2}
                                            className="w-full border border-black text-sm font-body p-2 rounded-none focus:outline-none"
                                            data-testid={`admin-design-note-${d.id}`}
                                        />
                                        <div className="flex gap-2">
                                            <button type="button" onClick={() => decide(d.id, "accept")} disabled={!!busy} className="inline-flex items-center gap-1 bg-green-700 text-white uppercase tracking-widest text-[10px] font-semibold px-3 h-9 disabled:opacity-40" data-testid={`admin-design-accept-${d.id}`}>
                                                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5}/> {busy === `accept-${d.id}` ? "Creating…" : "Accept + Create Order"}
                                            </button>
                                            <button type="button" onClick={() => decide(d.id, "reject")} disabled={!!busy} className="inline-flex items-center gap-1 border border-red-700 text-red-700 uppercase tracking-widest text-[10px] font-semibold px-3 h-9 hover:bg-red-700 hover:text-white disabled:opacity-40" data-testid={`admin-design-reject-${d.id}`}>
                                                <XCircle className="h-3.5 w-3.5" strokeWidth={1.5}/> {busy === `reject-${d.id}` ? "Rejecting…" : "Reject"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {d.admin_note && (
                                    <div className="p-3 border border-black bg-white text-xs font-body">
                                        <div className="overline text-neutral-500 mb-1">Your decision note</div>
                                        {d.admin_note}
                                    </div>
                                )}
                                <MessageThread threadType="design" threadId={d.id}/>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
